import axiosInstance from "../../setupAxios";
import { IStoragePath } from "../../dataingestion/types/IStoragePath";
import { IDataSourceBase } from "../../home/types/JobParameterControls/IDataSource";
import { IDistribution } from "../../repertoire/types/usageTypes/IDistribution";
import { IContributorMassUpdateParameters } from "../types/IContributorMassUpdateParameters";
import { IScheduledJobState } from "../types/IScheduledJobState";
import { ITriggerManualJob } from "../types/ITriggerManualJob";
import { ICwrgParameters } from "../types/ICwrgParameters";
import {BATCH_CONSOLIDATION_JOB_TYPE, PRE_ALLOCATION_JOB_TYPE} from "../../home/Consts";

export class JobService {

    static postWorkBatch(workBatchID: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("job/postWorkBatch/" + workBatchID)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static matchWorkBatch(workBatchID: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("job/matchWorkBatch/" + workBatchID)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static postProductBatch(productBatchID: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("job/postProductBatch/" + productBatchID)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static matchProductBatch(productBatchID: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("job/matchProductBatch/" + productBatchID)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static postAgreementBatch(agreementBatchID: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("job/postAgreementBatch/" + agreementBatchID)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static postAdjustmentBatch(adjustmentBatchID: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("job/postAdjustmentBatch/" + adjustmentBatchID)
                .then(res => {
                    resolve(res);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                });
        });
    }

    static matchIngestionFile(storagePath: IStoragePath): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            axiosInstance.post(encodeURI(`job/matchIngestionFile`), storagePath)
                .then(res => {
                    resolve(res.data);
                });
        });
    }

    static triggerContributorMassUpdate(parameters: IContributorMassUpdateParameters): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            axiosInstance.post(encodeURI(`job/contributorMassUpdate`), parameters)
                .then(res => {
                    resolve(res.data);
                });
        });
    }

    static getJobParameters(key: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/jobParameter/" + key)
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }

    static allocateDistributionPools(distribution: IDistribution, undoAllocation: boolean): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post('job/allocateDistributionPools/', { distribution, undoAllocation })
                .then(response => {
                    resolve(response);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static preAllocateDistributionPools(distribution: IDistribution, undoPreAllocation: boolean): Promise<any> {
        const manualJobParameters: ITriggerManualJob = {
            jobType: PRE_ALLOCATION_JOB_TYPE,
            jobParameters: JSON.stringify({
                distributionCode: distribution.distributionCode,
                distributionId: distribution.distributionID,
                pools: distribution.distributionPools.map(pool => pool.poolID),
                poolCodes: distribution.distributionPools.map(pool => pool.poolCode),
                undoPreAllocation: undoPreAllocation
            }),
        };
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post('job/triggerManualJob/', manualJobParameters)
                .then(response => {
                    resolve(response);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static runQAOnDistributionPools(distribution: IDistribution, undoQA: boolean): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post('job/runQAOnDistributionPools/', { distribution, undoQA })
                .then(response => {
                    resolve(response);
                })
                .catch(err => {
                    err.response.data.messages = err.response.data.messages.map(message => message.replace("Status BadRequest - ", ""));
                    reject(err.response.data);
                })
        })
    }

    static triggerManualJob(manualJob: ITriggerManualJob): Promise<any> {
        if (typeof manualJob.jobParameters === "string") {
            try {
                const params = JSON.parse(manualJob.jobParameters);
                if (Array.isArray(params.controlList)) {
                    params.controlList = params.controlList.map(({ DataSourceRepo, ...rest }) => rest);
                }
                manualJob.jobParameters = JSON.stringify(params);
            } catch (e) {
                // If parsing fails, proceed as is because we do not have a DataSourceRepo to remove
            }
        }

        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post('job/triggerManualJob/', manualJob)
                .then(response => {
                    resolve(response);
                })
                .catch(err => {
                    let errorMessage = (err !== "saveFailedMessage") ? err : `${manualJob.jobType} job has failed to start. Please try again or contact support if the issue persists.`;
                    reject(errorMessage);
                })
        })
    }

    static getUsageFilenames(poolValue:string,distributionValue:string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get(`job/usageFileNames?distributionCode=${encodeURIComponent(distributionValue)}&poolCode=${encodeURIComponent(poolValue)}`)
                .then(response => {
                    resolve(response);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static getAllUsageFilenamesFromDistribution(distributionValue:string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get(`job/usageFileNames?distributionCode=${encodeURIComponent(distributionValue)}`)
                .then(response => {
                    resolve(response);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static getCWRAckHWM(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/cwrAckHWM/")
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }

    static getAutomaticAgreementGHWM(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/aagHwm/")
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }
    static getIceATF(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/iceATF/")
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }

    static getEmuwHwm(distributionId: number): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/emuwHwm?distributionId=" + distributionId)
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }

    static getManualJobDataSources(): Promise<{[key: string]: IDataSourceBase}> {
        return axiosInstance.get<{[key: string]: IDataSourceBase}>('job/getManualJobDataSources/')
            .then(response => {
                return response.data;
            })
            .catch(error => {
                let tst = error;
                return null;
            });
    }

    public static getScheduledJob(jobType: string, jobParameters: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/scheduledJob?jobType=" + jobType + "&jobParameters=" + jobParameters)
                .then(response => {
                    const result = response.data;
                    resolve(result);
                })
                .catch(err => {
                    reject(err);
                })
        })
    }

    public static getAllScheduledJobs(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/getAllScheduledJobs/")
                .then(response => {
                    const result = response.data;
                    resolve(result);
                })
                .catch(err => {
                    reject(err);
                })
        })
    }

        static deleteScheduledJob(schedule: IScheduledJobState): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post('job/deleteScheduledJob/', schedule)
                .then(response => {
                    const result = response.data;
                    resolve(result);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static upsertScheduledJob(schedule: IScheduledJobState): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post('job/upsertScheduledJob/', schedule)
                .then(response => {
                    const result = response.data;
                    resolve(result);
                })
                .catch(err => {
                    console.error(err);
                    reject(err);
                })
        })
    }

    static getCWRGHighWaterMark(cwrgParams: ICwrgParameters): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .post("job/cwrgHighWaterMark/", cwrgParams)
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        if (result === '') {
                            resolve(null);
                        }
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }

    static getDaspHighWaterMark(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/daspHighWaterMark/")
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        if (result === '') {
                            resolve(null);
                        }
                        resolve(result);
                    }
                    else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }
    static getDtsHighWaterMark(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            axiosInstance
                .get("job/dtsHighWaterMark/")
                .then(response => {
                    if (response.status === 200) {
                        const result = response.data;
                        if (result === '') {
                            resolve(null);
                        }
                        resolve(result);
                    } else {
                        throw response;
                    }
                })
                .catch(err => {
                    reject(err.response.status);
                });
        });
    }

    static checkJobsInProgress(): Promise<boolean> {
        return axiosInstance
            .get("job/jobsInProgress/")
            .then(response => {
                if (response.status === 200) {
                    return response.data; // true or false
                } else {
                    throw response;
                }
            })
            .catch(err => {
                throw err.response?.status || err;
            });
    }
}
